import { Panel, PanelProps } from 'react-resizable-panels';

import { DRAWER_WIDTH } from 'src/constants';

const MIN_WIDTH_IN_PIXELS = 288;
const DEFAULT_WIDTH_IN_PIXELS = 396;
const DESKTOP_MAX_WIDTH_PERCENTAGE = 55;
const MOBILE_MAX_WIDTH_PERCENTAGE = 100;
/**
 * Calculates responsive size constraints for the right panel based on viewport width.
 *
 * Mobile (< 600px): Returns 100% width
 * Desktop: Converts fixed pixel widths to percentages of available space
 *   - Min: 288px → percentage of (viewport width - 200px sidebar)
 *   - Default: 396px → percentage of (viewport width - 200px sidebar)
 *   - Max: 55% of available space
 *
 * @param viewPortWidth - Current viewport width in pixels
 * @returns Panel size configuration object with minSize, maxSize, and defaultSize in percentages
 */
export const getRightPanelSizeProps = (
  viewPortWidth: number,
  isMobile: boolean,
) => {
  if (isMobile) {
    return { minSize: MOBILE_MAX_WIDTH_PERCENTAGE };
  }

  // if viewPortWidth is not available, return default sizes
  if (!viewPortWidth) {
    return { minSize: 20, maxSize: 55, defaultSize: 30 };
  }

  // On narrow desktops/tablets the fixed pixel widths can exceed the max
  // percentage; clamp both so we never hand the panel library a size above
  // maxSize (which pins the handle at its ceiling with no room to grow).
  const minWidthInPercentage = Math.min(
    (MIN_WIDTH_IN_PIXELS / (viewPortWidth - DRAWER_WIDTH)) * 100,
    DESKTOP_MAX_WIDTH_PERCENTAGE,
  );
  const defaultWidthInPercentage = Math.min(
    (DEFAULT_WIDTH_IN_PIXELS / (viewPortWidth - DRAWER_WIDTH)) * 100,
    DESKTOP_MAX_WIDTH_PERCENTAGE,
  );
  return {
    minSize: minWidthInPercentage,
    maxSize: DESKTOP_MAX_WIDTH_PERCENTAGE,
    defaultSize: defaultWidthInPercentage,
  };
};

export const ResizablePanel = ({
  children,
  className,
  ...props
}: { children: React.ReactNode; className?: string } & PanelProps) => {
  return (
    <Panel {...props} className={className}>
      {children}
    </Panel>
  );
};
